// useLivestreamRatingHint.js
import { useRef, useEffect, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';

import {
  RATING_LIVESTREAM_VIEWER_TRIGGER_SHOW_UP_PERCENTAGE,
  RATING_LIVESTREAM_VIEWER_TRIGGER_DURATION_SECONDS,
  RATING_LIVESTREAM_VIEWER_TRIGGER_INTERVAL_SECONDS,
} from '../RemoteConfigKeys.js';

import { openLivestreamRatingHint } from '../action/livestreamRatingHintActions.js';
import fetchMeLivestreamRating from '../action/fetchMeLivestreamRating.js';

import usePreviousValue from '../hook/usePreviousValue.js';

import getOperationData from '../selector/getOperationData.js';
import getLiveStreamData from '../selector/getLiveStreamData.js';
import getRemoteConfigData from '../selector/getRemoteConfigData.js';
import getModalData from '../selector/getModalData.js';
import getMeData from '../selector/getMeData.js';

import { getIsRated } from '../resource/livestreamUtils.js';
import { livestream as livestreamDebug } from '../resource/debug.js';
import { drawOne } from '../resource/lotteryUtils.js';

const engagementSelectPath = ['engagement'];
const livestreamLog = livestreamDebug.extend('log:useLivestreamRatingHint');

const useLivestreamRatingHint = ({ streamId }) => {
  const dispatch = useDispatch();

  const drawCountRef = useRef(0);
  const playingDurationSecondsRef = useRef(0);
  const timerRef = useRef(null);

  const meId = useSelector(state => getMeData(state, 'id'));

  const probability = useSelector(state =>
    getRemoteConfigData(
      state,
      RATING_LIVESTREAM_VIEWER_TRIGGER_SHOW_UP_PERCENTAGE
    )
  );
  const triggerMinDurationSeconds = useSelector(state =>
    getRemoteConfigData(
      state,
      RATING_LIVESTREAM_VIEWER_TRIGGER_DURATION_SECONDS
    )
  );
  const triggerIntervalSeconds = useSelector(state =>
    getRemoteConfigData(
      state,
      RATING_LIVESTREAM_VIEWER_TRIGGER_INTERVAL_SECONDS
    )
  );

  const isPlaying = useSelector(state =>
    getOperationData(state, engagementSelectPath, 'isPlaying')
  );
  const isPlayerVisible = useSelector(state =>
    getOperationData(state, engagementSelectPath, 'isPlayerVisible')
  );

  const sessionId = useSelector(state =>
    getLiveStreamData(state, [streamId], 'sessionId')
  );
  const isRated = useSelector(state =>
    getIsRated({
      ratedAs: getLiveStreamData(
        state,
        [streamId, 'rating', sessionId],
        'ratedAs'
      ),
    })
  );

  const isRatingHintOpened = useSelector(state =>
    ['LivestreamRatingHint', 'LivestreamRatingModal']
      .map(id => getModalData(state, id, 'status'))
      .some(status => ['display', 'opening'].includes(status))
  );

  const prevSessionId = usePreviousValue(sessionId);

  const reset = useCallback(() => {
    const log = livestreamLog.extend('reset');

    log('run reset function');

    playingDurationSecondsRef.current = 0;
    drawCountRef.current = 0;

    log('reset data finished');
  }, []);

  // Fetch me livestream rating
  useEffect(() => {
    if (streamId && sessionId && !isRated) {
      dispatch(fetchMeLivestreamRating({ streamId }));
    }
  }, [dispatch, isRated, sessionId, streamId, meId]); // The purpose of meId is to allow this effect to be retriggered when a user logs into the livestream

  // Reset on session id change
  useEffect(() => {
    const log = livestreamLog.extend('resetPlayingDuration');

    log('run reset playing duration effect when session id changed', {
      sessionId,
      prevSessionId,
    });

    if (!!sessionId && !!prevSessionId && prevSessionId !== sessionId) {
      reset();
    }
  }, [prevSessionId, reset, sessionId]);

  // Record playing duration and try open rating hint
  useEffect(() => {
    const log = livestreamLog.extend('startPlayingDurationTimer');

    const needRecordPlayingDuration =
      !!streamId &&
      isPlaying &&
      isPlayerVisible &&
      !isRated &&
      !isRatingHintOpened;

    log('run record playing duration timer effect', {
      streamId,
      isPlaying,
      isPlayerVisible,
      isRated,
      isRatingHintOpened,
      needRecordPlayingDuration,
    });

    if (needRecordPlayingDuration) {
      log('start record playing duration timer');

      timerRef.current = setInterval(() => {
        playingDurationSecondsRef.current += 1;

        const drawThresholdSeconds =
          drawCountRef.current > 0
            ? triggerIntervalSeconds
            : triggerMinDurationSeconds;

        const isThresholdReached =
          playingDurationSecondsRef.current >= drawThresholdSeconds;

        log('check draw threshold', {
          drawCount: drawCountRef.current,
          drawThresholdSeconds,
          playingDurationSeconds: playingDurationSecondsRef.current,
          isThresholdReached,
        });

        if (isThresholdReached) {
          playingDurationSecondsRef.current = 0;
          drawCountRef.current += 1;

          const drawResult = drawOne({ probability });

          log('draw result', { drawResult, probability });

          if (drawResult) {
            dispatch(openLivestreamRatingHint({ streamId, sessionId }));
          }
        }
      }, 1000);
    }

    return () => {
      clearInterval(timerRef.current);
      timerRef.current = null;

      log('record playing duration timer stopped', {
        streamId,
      });
    };
  }, [
    dispatch,
    isPlayerVisible,
    isPlaying,
    isRated,
    isRatingHintOpened,
    probability,
    sessionId,
    streamId,
    triggerIntervalSeconds,
    triggerMinDurationSeconds,
  ]);
};

export default useLivestreamRatingHint;
